// src/pages/marketplace/admin/AdminCategoriesPage.tsx

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

interface CategoryFormData {
  slug: string;
  name: string;
  description: string;
  icon_url: string;
  parent: string;
  color: string;
  display_order: number;
  is_active: boolean;
}

const EMPTY_FORM: CategoryFormData = {
  slug: '',
  name: '',
  description: '',
  icon_url: '',
  parent: '',
  color: '#6366f1',
  display_order: 0,
  is_active: true,
};

/**
 * Category management page for the product marketplace.
 * Route: /admin/products/categories
 */
export const AdminCategoriesPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();

  const [categories, setCategories] = useState<any[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<CategoryFormData>({ ...EMPTY_FORM });
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadCategories = async () => {
    const result = await store.ListProductCategories();
    if (result) {
      setCategories(Array.isArray(result) ? result : (result as any).items || []);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleChange = (field: keyof CategoryFormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditChange = (field: keyof CategoryFormData, value: string | number | boolean) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.slug || !formData.name) return;
    setIsSubmitting(true);
    try {
      await store.CreateProductCategory({
        ...formData,
        parent: formData.parent || undefined,
      } as any);
      setFormData({ ...EMPTY_FORM });
      setShowAddForm(false);
      await loadCategories();
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editFormData.slug || !editFormData.name) return;
    setIsSubmitting(true);
    try {
      await store.UpdateProductCategory(id, {
        ...editFormData,
        parent: editFormData.parent || undefined,
      } as any);
      setEditingId(null);
      await loadCategories();
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await store.DeleteProductCategory(id);
      setDeleteConfirm(null);
      await loadCategories();
    } catch {
      // Error handled by store
    }
  };

  const startEdit = (category: any) => {
    const catId = category.entity?.id || category.id;
    setEditingId(catId);
    setEditFormData({
      slug: category.slug ?? '',
      name: category.name ?? '',
      description: category.description ?? '',
      icon_url: category.icon_url ?? '',
      parent: category.parent?.id || category.parent || '',
      color: category.color ?? '#6366f1',
      display_order: category.display_order ?? 0,
      is_active: category.is_active ?? true,
    });
  };

  // Build tree structure from flat list
  const rootCategories = categories.filter(
    (c) => !c.parent || c.parent === '' || c.parent === null,
  );
  const getChildren = (parentId: string) =>
    categories.filter(
      (c) =>
        (c.parent?.id || c.parent) === parentId,
    );

  const renderCategoryRow = (category: any, depth: number = 0) => {
    const catId = category.entity?.id || category.id;
    const isEditing = editingId === catId;
    const children = getChildren(catId);

    return (
      <div key={catId}>
        <div
          className={`flex items-center gap-3 py-3 px-4 border-b border-border hover:bg-muted/50 transition-colors ${
            depth > 0 ? 'bg-muted/20' : ''
          }`}
          style={{ paddingLeft: `${16 + depth * 24}px` }}
        >
          {isEditing ? (
            /* Edit mode */
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Input
                  placeholder="Slug"
                  value={editFormData.slug}
                  onChange={(e) => handleEditChange('slug', e.target.value)}
                  fullWidth={false}
                  className="w-full"
                />
                <Input
                  placeholder="Name"
                  value={editFormData.name}
                  onChange={(e) => handleEditChange('name', e.target.value)}
                  fullWidth={false}
                  className="w-full"
                />
                <Input
                  placeholder="Icon URL"
                  value={editFormData.icon_url}
                  onChange={(e) => handleEditChange('icon_url', e.target.value)}
                  fullWidth={false}
                  className="w-full"
                />
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={editFormData.color}
                    onChange={(e) => handleEditChange('color', e.target.value)}
                    className="h-10 w-10 rounded border border-input cursor-pointer"
                  />
                  <Input
                    type="number"
                    placeholder="Order"
                    value={String(editFormData.display_order)}
                    onChange={(e) => handleEditChange('display_order', parseInt(e.target.value, 10) || 0)}
                    fullWidth={false}
                    className="w-20"
                  />
                </div>
              </div>
              <Textarea
                placeholder="Description"
                value={editFormData.description}
                onChange={(e) => handleEditChange('description', e.target.value)}
                rows={2}
              />
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editFormData.is_active}
                    onChange={(e) => handleEditChange('is_active', e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Active</span>
                </label>
                <Select
                  value={editFormData.parent}
                  onChange={(e) => handleEditChange('parent', e.target.value)} fullWidth={false}>
                  <option value="">No parent</option>
                  {categories
                    .filter((c) => (c.entity?.id || c.id) !== catId)
                    .map((c) => (
                      <option key={c.entity?.id || c.id} value={c.entity?.id || c.id}>
                        {c.name}
                      </option>
                    ))}
                </Select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleUpdate(catId)} disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            /* Display mode */
            <>
              <div
                className="h-4 w-4 rounded-full shrink-0"
                style={{ backgroundColor: category.color || '#6366f1' }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {String(category.name ?? '')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {String(category.slug ?? '')}
                  </span>
                  {!category.is_active && (
                    <Badge variant="secondary" size="sm">
                      Inactive
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="text-xs text-muted-foreground tabular-nums">
                  {category.product_count ?? 0} products
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  Order: {category.display_order ?? 0}
                </span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(category)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => setDeleteConfirm(catId)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Delete confirmation inline */}
        {deleteConfirm === catId && (
          <div className="px-4 py-3 bg-destructive/5 border-b border-border">
            <div className="flex items-center justify-between">
              <p className="text-sm text-foreground">
                This will remove{' '}
                <span className="font-semibold">{category.product_count ?? 0}</span> products from
                this category. Are you sure?
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDelete(catId)}
                >
                  Delete
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Render children recursively */}
        {children
          .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0))
          .map((child: any) => renderCategoryRow(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb and header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/admin/products" className="text-muted-foreground hover:text-foreground">
            Admin
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/admin/products" className="text-muted-foreground hover:text-foreground">
            Products
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Categories</span>
        </nav>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-foreground">Product Categories</h1>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? 'Cancel' : 'Add Category'}
        </Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Add category form */}
      {showAddForm && (
        <Card>
          <form onSubmit={handleCreate}>
            <CardHeader>
              <CardTitle>New Category</CardTitle>
              <CardDescription>
                Create a new product category for the marketplace.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Slug"
                  placeholder="e.g. productivity-tools"
                  value={formData.slug}
                  onChange={(e) => handleChange('slug', e.target.value)}
                  required
                />
                <Input
                  label="Name"
                  placeholder="e.g. Productivity Tools"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </div>
              <Textarea
                label="Description"
                placeholder="Category description..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Icon URL"
                  type="url"
                  placeholder="https://..."
                  value={formData.icon_url}
                  onChange={(e) => handleChange('icon_url', e.target.value)}
                />
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-foreground">Parent Category</label>
                  <Select
                    value={formData.parent}
                    onChange={(e) => handleChange('parent', e.target.value)} fullWidth={false}>
                    <option value="">None (top-level)</option>
                    {categories.map((c) => (
                      <option key={c.entity?.id || c.id} value={c.entity?.id || c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-foreground">Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => handleChange('color', e.target.value)}
                      className="h-10 w-10 rounded border border-input cursor-pointer"
                    />
                    <Input
                      placeholder="#6366f1"
                      value={formData.color}
                      onChange={(e) => handleChange('color', e.target.value)}
                      fullWidth={false}
                      className="flex-1"
                    />
                  </div>
                </div>
                <Input
                  label="Display Order"
                  type="number"
                  value={String(formData.display_order)}
                  onChange={(e) => handleChange('display_order', parseInt(e.target.value, 10) || 0)}
                />
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                    />
                    <span className="text-sm font-medium text-foreground">Active</span>
                  </label>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData({ ...EMPTY_FORM });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Category'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {/* Categories tree */}
      <Card>
        <CardContent className="p-0">
          {store.isLoading ? (
            <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>
          ) : categories.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No categories yet. Click &quot;Add Category&quot; to create one.
            </div>
          ) : (
            <div>
              {rootCategories
                .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0))
                .map((cat: any) => renderCategoryRow(cat))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default AdminCategoriesPage;
